/**
 * 内存存储：测试用后端，同时也是"存储不可用"时的降级目标。
 *
 * 它**必须尽可能地像 IndexedDB**，否则降级路径会在用户机器上表现出测试里没见过的行为：
 *
 * 1. **写入时深拷贝，读取时再深拷贝一份。** IndexedDB 在结构化克隆，写进去之后再改原对象
 *    不会影响已存的内容。如果内存实现直接存引用，测试里"存了再改"的错误写法会绿，
 *    而真实后端读出来的却是旧值 —— 这类偏差只会在用户那里出现，而且看起来像"数据没保存"。
 * 2. **键按字典序返回。** 与 IndexedDB 的 `getAllKeys` 一致（见 store.ts）。
 * 3. **配额用同形状的错误抛。** IndexedDB 在配额满时抛 `QuotaExceededError`（DOMException）。
 *    内存实现抛同样形状的错误，上层的识别与提示逻辑才真的被测到。
 * 4. **`batch()` 全有或全无。** 内存实现比真实后端更容易写出"改了一半"的实现。
 *
 * 容量是可选的：不设上限时它不做配额判断（测试里绝大多数场景不关心配额）。
 */

import { createQuotaExceededError, StorageUnavailableError } from './errors';
import { assertStorable, estimateStorableBytes } from './serialization';
import {
  PERSISTENCE_COLLECTIONS,
  selectKeys,
  type KeyValueStore,
  type PersistenceCollection,
  type StoreKind,
  type StoreOperation,
} from './store';

export interface MemoryStoreOptions {
  /**
   * 估算字节上限。达到上限后写入抛 `QuotaExceededError`，用于在测试里复现"配额满"，
   * 也让降级路径下的用户体验与真实后端一致（一样会提示清理历史记录）。
   */
  readonly byteCapacity?: number;
}

export class MemoryStore implements KeyValueStore {
  readonly kind: StoreKind = 'memory';

  private readonly byteCapacity: number | undefined;
  private data = new Map<PersistenceCollection, Map<string, unknown>>();
  private bytes = 0;
  private closed = false;

  constructor(options: MemoryStoreOptions = {}) {
    this.byteCapacity = options.byteCapacity;
    for (const collection of PERSISTENCE_COLLECTIONS) this.data.set(collection, new Map());
  }

  async get(collection: PersistenceCollection, key: string): Promise<unknown | undefined> {
    this.assertOpen();
    const record = this.data.get(collection)?.get(key);
    // 与 IndexedDB 一样，读出来的是一份副本，调用方改它不会改到已存的数据
    return record === undefined ? undefined : structuredClone(record);
  }

  async put(collection: PersistenceCollection, key: string, value: unknown): Promise<void> {
    this.assertOpen();
    this.bytes += this.apply(this.data, [{ kind: 'put', collection, key, value }]);
  }

  async remove(collection: PersistenceCollection, key: string): Promise<void> {
    this.assertOpen();
    this.bytes += this.apply(this.data, [{ kind: 'delete', collection, key }]);
  }

  async keys(collection: PersistenceCollection, prefix?: string): Promise<readonly string[]> {
    this.assertOpen();
    return selectKeys([...(this.data.get(collection)?.keys() ?? [])], prefix);
  }

  async batch(operations: readonly StoreOperation[]): Promise<void> {
    this.assertOpen();
    // 先在副本上跑完，成功再整体替换 —— 失败时内存里必须是改动前的样子
    const draft = new Map<PersistenceCollection, Map<string, unknown>>();
    for (const [collection, records] of this.data) draft.set(collection, new Map(records));
    this.bytes += this.apply(draft, operations);
    this.data = draft;
  }

  async close(): Promise<void> {
    this.closed = true;
    this.data = new Map();
    this.bytes = 0;
  }

  /** 当前估算占用，仅用于测试与诊断 */
  estimatedBytes(): number {
    return this.bytes;
  }

  /** 目标记录在给定映射里的估算体积；不存在时为 0 */
  private sizeOf(
    target: Map<PersistenceCollection, Map<string, unknown>>,
    collection: PersistenceCollection,
    key: string,
  ): number {
    const existing = target.get(collection)?.get(key);
    return existing === undefined ? 0 : estimateStorableBytes(existing);
  }

  /**
   * 在给定的映射上应用一批操作，返回估算字节的**增量**。
   *
   * 增量按"新值 - 被替换/删除的旧值"算：删除必须真的把空间还回来，
   * 否则模拟配额的测试里"轮转腾出空间"这件事永远不会发生，
   * 而没有这条路径，配额相关的降级逻辑就只在一半的分支上被测到。
   * 任一条非法（不可序列化/超配额）就整批抛出，不做部分写入。
   */
  private apply(
    target: Map<PersistenceCollection, Map<string, unknown>>,
    operations: readonly StoreOperation[],
  ): number {
    let delta = 0;
    for (const operation of operations) {
      if (operation.kind === 'put') assertStorable(operation.value, `${operation.collection}/${operation.key}`);
      const removed = this.sizeOf(target, operation.collection, operation.key);
      const added = operation.kind === 'put' ? estimateStorableBytes(operation.value) : 0;
      delta += added - removed;
    }

    if (this.byteCapacity !== undefined && this.bytes + delta > this.byteCapacity) {
      throw createQuotaExceededError(
        `内存存储估算占用 ${this.bytes + delta} 字节，超过上限 ${this.byteCapacity} 字节`,
      );
    }

    for (const operation of operations) {
      const collection = target.get(operation.collection);
      if (collection === undefined) throw new Error(`未知集合：${operation.collection}`);
      if (operation.kind === 'delete') {
        collection.delete(operation.key);
        continue;
      }
      collection.set(operation.key, structuredClone(operation.value));
    }
    return delta;
  }

  private assertOpen(): void {
    if (this.closed) throw new StorageUnavailableError('内存存储已关闭');
  }
}
